import { useState, useCallback, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import { RequirePermission } from "@/components/RequirePermission";
import { useBills, useApproveBill, useMarkBillPaid, useVoidBill, useHardDeleteBill } from "@/hooks/useBills";
import { useProjects } from "@/hooks/useProjects";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invalidateFinancials } from "@/lib/invalidateProjectFinancials";
import { cn } from "@/lib/utils";
import { usePagination } from "@/hooks/usePagination";
import { usePermissions } from "@/hooks/usePermissions";
import { PaginationControls } from "@/components/controller/PaginationControls";
import { StatusBadge } from "@/components/controller/StatusBadge";
import { APAgingReport } from "@/components/controller/APAgingReport";
import { DataTable } from "@/components/shared/DataTable";
import { BillUploadDialog } from "@/components/controller/BillUploadDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { type ColumnDef } from "@tanstack/react-table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, CheckCircle, DollarSign, XCircle, AlertTriangle, Printer, Upload, Trash2, ChevronDown, ChevronRight, Building2, Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import type { Bill } from "@/hooks/useBills";
import { ExportToolbar } from "@/components/shared/ExportToolbar";
import { EntityContextMenu } from "@/components/shared/EntityContextMenu";
import { BillDetailModal } from "@/components/controller/BillDetailModal";
import { exportPdf } from "@/lib/export";
import { BillPrintTemplate } from "@/components/shared/print-templates/BillPrintTemplate";
import { groupBillLinesForPrint } from "@/lib/groupBillLinesForPrint";

const fmt = (n: number) =>
  `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

// ── Mark as Paid Dialog ──────────────────────────────────────────────────
function MarkPaidDialog({
  bill,
  open,
  onClose,
}: {
  bill: Bill | null;
  open: boolean;
  onClose: () => void;
}) {
  const [ref, setRef] = useState("");
  const [paidDate, setPaidDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [paymentMethod, setPaymentMethod] = useState("ach");
  const [amount, setAmount] = useState("");
  const markPaid = useMarkBillPaid();

  // Reset amount when bill changes
  const billTotal = bill?.total ?? 0;
  const effectiveAmount = amount === "" ? billTotal : Number(amount);
  const isPartial = effectiveAmount > 0 && effectiveAmount < billTotal;

  const handleSubmit = async () => {
    if (!bill) return;
    try {
      await markPaid.mutateAsync({
        billId: bill.id,
        paymentReference: ref.trim() || undefined,
        paidAt: paidDate ? new Date(paidDate).toISOString() : undefined,
        paymentMethod,
        amountPaid: effectiveAmount,
      });
      toast.success(`Bill ${bill.bill_number} ${isPartial ? "partially " : ""}marked as paid`);
      onClose();
      setRef("");
      setAmount("");
      setPaymentMethod("ach");
    } catch (e: any) {
      toast.error(e.message ?? "Failed to mark bill as paid");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            Bill <span className="font-mono font-semibold">{bill?.bill_number}</span> — Total {fmt(billTotal)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="paid-date">Payment Date</Label>
            <Input
              id="paid-date"
              type="date"
              value={paidDate}
              onChange={(e) => setPaidDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pay-method">Payment Method</Label>
            <select
              id="pay-method"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <option value="ach">ACH</option>
              <option value="check">Check</option>
              <option value="wire">Wire Transfer</option>
              <option value="credit_card">Credit Card</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pay-amount">Amount</Label>
            <Input
              id="pay-amount"
              type="number"
              step="0.01"
              min="0.01"
              max={billTotal}
              placeholder={fmt(billTotal)}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            {isPartial && (
              <p className="text-xs text-amber-600">Partial payment — bill will be marked as partially paid.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ref">Payment Reference (check #, confirmation, etc.)</Label>
            <Input
              id="ref"
              placeholder="Optional"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={markPaid.isPending || effectiveAmount <= 0}>
            <DollarSign className="h-4 w-4 mr-1" />
            {markPaid.isPending ? "Saving…" : isPartial ? "Record Partial Payment" : "Confirm Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Void Dialog ───────────────────────────────────────────────────────────
function VoidDialog({
  bill,
  open,
  onClose,
}: {
  bill: Bill | null;
  open: boolean;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const voidBill = useVoidBill();

  const handleVoid = async () => {
    if (!bill || !reason.trim()) return;
    try {
      await voidBill.mutateAsync({ billId: bill.id, reason: reason.trim() });
      toast.success(`Bill ${bill.bill_number} voided`);
      onClose();
      setReason("");
    } catch (e: any) {
      toast.error(e.message ?? "Failed to void bill");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4" /> Void Bill
          </DialogTitle>
          <DialogDescription>
            Void bill <span className="font-mono font-semibold">{bill?.bill_number}</span>. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-2">
          <Label htmlFor="void-reason">Reason for voiding <span className="text-destructive">*</span></Label>
          <Textarea
            id="void-reason"
            placeholder="Describe why this bill is being voided…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={!reason.trim() || voidBill.isPending}
            onClick={handleVoid}
          >
            <XCircle className="h-4 w-4 mr-1" />
            {voidBill.isPending ? "Voiding…" : "Void Bill"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Approve Confirm ───────────────────────────────────────────────────────
function ApproveConfirm({
  bill,
  open,
  onClose,
}: {
  bill: Bill | null;
  open: boolean;
  onClose: () => void;
}) {
  const approve = useApproveBill();

  const handleApprove = async () => {
    if (!bill) return;
    try {
      await approve.mutateAsync(bill.id);
      toast.success(`Bill ${bill.bill_number} approved`);
      onClose();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to approve bill");
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Approve Bill?</AlertDialogTitle>
          <AlertDialogDescription>
            Approve bill <span className="font-mono font-semibold">{bill?.bill_number}</span> for {fmt(bill?.total ?? 0)}?
            This will mark it as approved and ready for payment.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleApprove} disabled={approve.isPending}>
            <CheckCircle className="h-4 w-4 mr-1" />
            {approve.isPending ? "Approving…" : "Approve"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ── Hard Delete Confirm ───────────────────────────────────────────────────
function HardDeleteDialog({
  bill,
  open,
  onClose,
}: {
  bill: Bill | null;
  open: boolean;
  onClose: () => void;
}) {
  const hardDelete = useHardDeleteBill();

  const handleDelete = async () => {
    if (!bill) return;
    try {
      await hardDelete.mutateAsync(bill.id);
      toast.success(`${bill.bill_number} permanently deleted`);
      onClose();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to delete bill");
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-4 w-4" /> Permanently Delete Bill?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This will <strong>permanently remove</strong>{" "}
            <span className="font-mono font-semibold">{bill?.bill_number}</span> and all its line items from the database.
            Any linked work order lines will be restored to billable status.
            This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={hardDelete.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            {hardDelete.isPending ? "Deleting…" : "Delete Permanently"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ── Print helper — fetches line items then opens print window ─────────────
async function printBill(bill: Bill, companyProfile?: { logo_url?: string | null; name?: string; address?: string | null; city?: string | null; state?: string | null; zip?: string | null } | null) {
  const { data: lines } = await supabase
    .from("bill_lines")
    .select("description, quantity, unit_price, line_total")
    .eq("bill_id", bill.id)
    .order("created_at");

  const lineItems = groupBillLinesForPrint(lines ?? []);


  exportPdf(
    <BillPrintTemplate
      data={{
        bill_number: bill.bill_number,
        bill_date: bill.bill_date,
        due_date: bill.due_date,
        status: bill.status,
        subtotal: bill.subtotal,
        retainage_amount: bill.retainage_amount,
        retainage_percent: bill.retainage_percent,
        total: bill.total,
        vendor_name: bill.vendors?.name,
        project_name: bill.projects?.name,
        project_number: bill.projects?.project_number,
        company_name: companyProfile?.name,
        company_logo_url: companyProfile?.logo_url,
        company_address: companyProfile?.address,
        company_city: companyProfile?.city,
        company_state: companyProfile?.state,
        company_zip: companyProfile?.zip,
        description: bill.description,
        payment_terms: (bill as any).payment_terms,
        line_items: lineItems,
      }}
    />,
    `Bill ${bill.bill_number}`
  );
}

// ── Bill Actions Dropdown ─────────────────────────────────────────────────
function BillActions({
  bill,
  onApprove,
  onMarkPaid,
  onVoid,
  onHardDelete,
  companyProfile,
}: {
  bill: Bill;
  onApprove: (b: Bill) => void;
  onMarkPaid: (b: Bill) => void;
  onVoid: (b: Bill) => void;
  onHardDelete: (b: Bill) => void;
  companyProfile?: { logo_url?: string | null; name?: string; address?: string | null; city?: string | null; state?: string | null; zip?: string | null } | null;
}) {
  const { can, isAdmin } = usePermissions();
  const canApprove = isAdmin || can("approve_bills");
  const canPay = isAdmin || can("approve_payments");

  const isVoided = bill.status === "voided";
  const isPaid = bill.status === "paid";
  const canApproveAction = canApprove && (bill.status === "draft" || bill.status === "pending");
  const canMarkPaidAction = canPay && (bill.status === "approved" || bill.status === "partially_paid");
  const canVoid = isAdmin || can("void_bills");
  const canVoidAction = canVoid && !isVoided && !isPaid;

  if (!canApproveAction && !canMarkPaidAction && !canVoidAction && !isAdmin) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        title="Print / PDF"
        onClick={() => printBill(bill, companyProfile)}
      >
        <Printer className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={() => printBill(bill, companyProfile)}>
          <Printer className="h-4 w-4 mr-2" /> Print / PDF
        </DropdownMenuItem>
        {canApproveAction && (
          <DropdownMenuItem onClick={() => onApprove(bill)}>
            <CheckCircle className="h-4 w-4 mr-2 text-green-600" /> Approve
          </DropdownMenuItem>
        )}
        {canMarkPaidAction && (
          <DropdownMenuItem onClick={() => onMarkPaid(bill)}>
            <DollarSign className="h-4 w-4 mr-2 text-blue-600" /> Mark as Paid
          </DropdownMenuItem>
        )}
        {canVoidAction && (
          <>
            {(canApproveAction || canMarkPaidAction) && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={() => onVoid(bill)}
              className="text-destructive focus:text-destructive"
            >
              <XCircle className="h-4 w-4 mr-2" /> Void Bill
            </DropdownMenuItem>
          </>
        )}
        {isAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onHardDelete(bill)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Hard Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Project Allocator (inline dropdown for unassigned bills) ─────────────
function ProjectAllocator({ bill }: { bill: Bill }) {
  const { data: projects } = useProjects();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);

  const handleAllocate = async (projectId: string) => {
    setSaving(true);
    try {
      const { error } = await supabase.rpc("update_bill_status" as any, {
        p_bill_id: bill.id,
        p_updates: { project_id: projectId, needs_project_assignment: false },
      });
      if (error) throw error;
      toast.success(`Bill ${bill.bill_number} allocated to project`);
      invalidateFinancials(qc, { billId: bill.id, projectId });
    } catch (e: any) {
      toast.error(e.message ?? "Failed to allocate bill");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Select onValueChange={handleAllocate} disabled={saving}>
      <SelectTrigger className="h-7 w-[180px] text-xs border-amber-300 bg-amber-50 dark:bg-amber-950/20">
        <SelectValue placeholder="Assign to project…" />
      </SelectTrigger>
      <SelectContent>
        {(projects || []).map((p: any) => (
          <SelectItem key={p.id} value={p.id} className="text-xs">
            {p.project_number ? `${p.project_number} — ` : ""}{p.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ── Bills Summary Tab ─────────────────────────────────────────────────────
// Fetches ALL bills (unpaginated) so the summary totals are always complete.
function useBillsAll() {
  return useQuery<Bill[]>({
    queryKey: ["bills", "all"],
    queryFn: async () => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return [];
      const { data, error } = await supabase
        .from("bills")
        .select("id, bill_number, bill_date, due_date, status, subtotal, retainage_amount, retainage_percent, total, description, created_at, paid_at, payment_reference, void_reason, project_id, vendor_id, contractor_id, work_order_id, needs_project_assignment, source, projects(name, project_number), vendors(name)")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Bill[];
    },
  });
}

interface VendorGroup {
  vendorId: string | null;
  vendorName: string;
  bills: Bill[];
  totalBilled: number;   // sum of subtotals (excl. voided)
  totalRetainage: number;
  totalNet: number;      // total after retainage (excl. voided)
  totalPaid: number;     // sum of paid bills
  balance: number;       // totalNet − totalPaid
}

function BillsSummaryTab({
  onBillClick,
}: {
  onBillClick: (b: Bill) => void;
}) {
  const { data: allBills, isLoading } = useBillsAll();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const groups = useMemo<VendorGroup[]>(() => {
    if (!allBills) return [];
    const map = new Map<string, VendorGroup>();

    for (const bill of allBills) {
      const key = bill.vendor_id ?? "__none__";
      if (!map.has(key)) {
        map.set(key, {
          vendorId: bill.vendor_id,
          vendorName: bill.vendors?.name ?? "No Vendor",
          bills: [],
          totalBilled: 0,
          totalRetainage: 0,
          totalNet: 0,
          totalPaid: 0,
          balance: 0,
        });
      }
      const g = map.get(key)!;
      g.bills.push(bill);
      const isVoided = bill.status === "voided";
      if (!isVoided) {
        g.totalBilled += Number(bill.subtotal);
        g.totalRetainage += Number(bill.retainage_amount);
        g.totalNet += Number(bill.total);
        if (bill.status === "paid" || bill.status === "partially_paid") {
          g.totalPaid += Number((bill as any).amount_paid ?? bill.total);
        }
      }
    }

    for (const g of map.values()) {
      g.balance = g.totalNet - g.totalPaid;
    }

    return Array.from(map.values()).sort((a, b) =>
      a.vendorName.localeCompare(b.vendorName)
    );
  }, [allBills]);

  // Summary totals across all vendors
  const grandTotals = useMemo(() => ({
    billed: groups.reduce((s, g) => s + g.totalBilled, 0),
    retainage: groups.reduce((s, g) => s + g.totalRetainage, 0),
    net: groups.reduce((s, g) => s + g.totalNet, 0),
    paid: groups.reduce((s, g) => s + g.totalPaid, 0),
    balance: groups.reduce((s, g) => s + g.balance, 0),
  }), [groups]);

  const toggle = (key: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  if (isLoading) return (
    <div className="space-y-2 pt-2">
      {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
    </div>
  );

  if (!groups.length) return (
    <div className="text-center py-12 text-muted-foreground text-sm">No bills found.</div>
  );

  return (
    <div className="space-y-3 pt-2">
      {/* Grand total banner */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Billed to Date", value: grandTotals.billed, color: "" },
          { label: "Retainage Held", value: grandTotals.retainage, color: "text-amber-600" },
          { label: "Total Paid", value: grandTotals.paid, color: "text-green-600" },
          { label: "Available Balance", value: grandTotals.balance, color: grandTotals.balance > 0 ? "text-blue-600" : "text-destructive" },
        ].map(({ label, value, color }) => (
          <div key={label} className="rounded-lg border bg-card px-4 py-3">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={`text-lg font-bold font-mono mt-0.5 ${color}`}>{fmt(value)}</p>
          </div>
        ))}
      </div>

      {/* Vendor accordion rows */}
      <div className="rounded-md border divide-y">
        {/* Header */}
        <div className="grid grid-cols-[1fr_auto] md:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] px-4 py-2 bg-muted/50 text-xs font-medium text-muted-foreground">
          <span>Vendor</span>
          <span className="hidden md:block text-right">Billed</span>
          <span className="hidden md:block text-right">Retainage</span>
          <span className="hidden md:block text-right">Net Total</span>
          <span className="hidden md:block text-right">Paid</span>
          <span className="text-right">Balance</span>
        </div>

        {groups.map((g) => {
          const key = g.vendorId ?? "__none__";
          const isOpen = expanded.has(key);
          const activeBills = g.bills.filter(b => b.status !== "voided");
          const voidedCount = g.bills.length - activeBills.length;

          return (
            <div key={key}>
              {/* Summary row */}
              <button
                className="w-full grid grid-cols-[1fr_auto] md:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] items-center px-4 py-3 hover:bg-muted/30 transition-colors text-sm text-left gap-x-4"
                onClick={() => toggle(key)}
              >
                <span className="flex items-center gap-2 font-medium truncate">
                  {isOpen
                    ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                  <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{g.vendorName}</span>
                  <Badge variant="secondary" className="text-xs shrink-0">{g.bills.length}</Badge>
                  {voidedCount > 0 && (
                    <span className="text-xs text-muted-foreground shrink-0">({voidedCount} voided)</span>
                  )}
                </span>
                <span className="hidden md:block text-right font-mono">{fmt(g.totalBilled)}</span>
                <span className="hidden md:block text-right font-mono text-amber-600">({fmt(g.totalRetainage)})</span>
                <span className="hidden md:block text-right font-mono font-semibold">{fmt(g.totalNet)}</span>
                <span className="hidden md:block text-right font-mono text-green-600">{fmt(g.totalPaid)}</span>
                <span className={`text-right font-mono font-semibold ${g.balance > 0 ? "text-blue-600" : g.balance < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                  {fmt(g.balance)}
                </span>
              </button>

              {/* Expanded bill list */}
              {isOpen && (
                <div className="bg-muted/20 border-t divide-y">
                  {g.bills.map((bill) => (
                    <button
                      key={bill.id}
                      className={cn(
                        "w-full flex items-center justify-between px-8 py-2.5 text-sm hover:bg-muted/40 transition-colors text-left gap-4",
                        bill.status === "voided" && "opacity-40"
                      )}
                      onClick={() => onBillClick(bill)}
                    >
                      <span className="flex items-center gap-3 min-w-0">
                        <span className="font-mono text-xs text-muted-foreground shrink-0">
                          {bill.bill_date ? format(new Date(bill.bill_date), "MMM d, yyyy") : "—"}
                        </span>
                        <span className="font-mono font-medium truncate">{bill.bill_number}</span>
                        {bill.projects?.name && (
                          <span className="text-xs text-muted-foreground truncate hidden sm:block">
                            {bill.projects.name}
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-4 shrink-0">
                        <StatusBadge status={bill.status} />
                        <span className="font-mono font-medium text-right w-24">{fmt(Number(bill.total))}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────
const BillsInner = () => {
  const pagination = usePagination();
  const [searchTerm, setSearchTerm] = useState("");
  const [projectFilter, setProjectFilter] = useState<"all" | "assigned" | "unassigned">("all");
  const [vendorFilter, setVendorFilter] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const billFilters = useMemo(() => ({
    search: searchTerm,
    projectFilter,
    vendorSearch: vendorFilter,
    amountMin: amountMin === "" ? undefined : Number(amountMin),
    amountMax: amountMax === "" ? undefined : Number(amountMax),
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  }), [searchTerm, projectFilter, vendorFilter, amountMin, amountMax, dateFrom, dateTo]);

  // Reset to page 1 when filters change
  useEffect(() => {
    pagination.setPage(1);
  }, [searchTerm, projectFilter, vendorFilter, amountMin, amountMax, dateFrom, dateTo]);

  const clearFilters = () => {
    setSearchTerm("");
    setProjectFilter("all");
    setVendorFilter("");
    setAmountMin("");
    setAmountMax("");
    setDateFrom("");
    setDateTo("");
  };

  const hasActiveFilters =
    !!searchTerm || projectFilter !== "all" || !!vendorFilter ||
    amountMin !== "" || amountMax !== "" || !!dateFrom || !!dateTo;

  const { data: bills, isLoading, isError } = useBills(pagination, billFilters);
  const { data: companyProfile } = useCompanyProfile();

  const [approveTarget, setApproveTarget] = useState<Bill | null>(null);
  const [paidTarget, setPaidTarget] = useState<Bill | null>(null);
  const [voidTarget, setVoidTarget] = useState<Bill | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Bill | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detailBill, setDetailBill] = useState<Bill | null>(null);

  // Auto-open bill detail from query param
  const [searchParams, setSearchParams] = useSearchParams();
  const billIdParam = searchParams.get("billId");
  useEffect(() => {
    if (billIdParam && bills && bills.length > 0) {
      const matched = bills.find((b) => b.id === billIdParam);
      if (matched) {
        setDetailBill(matched);
        searchParams.delete("billId");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [billIdParam, bills]);

  const needsAssignmentCount = useMemo(
    () => bills?.filter((b: any) => b.needs_project_assignment).length ?? 0,
    [bills]
  );

  const billColumns = useMemo<ColumnDef<Bill, any>[]>(() => [
    { accessorKey: "bill_number", header: "Bill #", cell: ({ getValue }) => <span className="font-mono text-sm">{getValue()}</span> },
    {
      id: "project", header: "Project",
      accessorFn: (row) => row.projects?.name ?? "—",
      cell: ({ row }) => {
        const needsAssignment = (row.original as any).needs_project_assignment;
        if (needsAssignment && !row.original.projects?.name) {
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <ProjectAllocator bill={row.original} />
            </div>
          );
        }
        return (
          <span className="text-sm flex items-center gap-1.5">
            {row.original.projects?.name ?? "—"}
            {row.original.projects?.project_number && <span className="text-muted-foreground ml-1 text-xs">({row.original.projects.project_number})</span>}
          </span>
        );
      },
    },
    { id: "vendor", header: "Vendor", accessorFn: (row) => row.vendors?.name ?? "—" },
    { accessorKey: "subtotal", header: "Subtotal", cell: ({ getValue }) => <span className="text-right block text-sm">{fmt(getValue())}</span> },
    { accessorKey: "retainage_amount", header: "Retainage", cell: ({ getValue }) => <span className="text-right block text-sm text-muted-foreground">-{fmt(getValue())}</span> },
    { accessorKey: "total", header: "Total", cell: ({ getValue }) => <span className="text-right block font-medium text-sm">{fmt(getValue())}</span> },
    { accessorKey: "bill_date", header: "Date", cell: ({ getValue }) => <span className="text-sm">{getValue() ? format(new Date(getValue()), "MMM d, yyyy") : "—"}</span> },
    { accessorKey: "status", header: "Status", cell: ({ getValue }) => <StatusBadge status={getValue()} /> },
    {
      id: "actions", header: "", enableSorting: false, enableColumnFilter: false, size: 40,
      cell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <BillActions bill={row.original} onApprove={setApproveTarget} onMarkPaid={setPaidTarget} onVoid={setVoidTarget} onHardDelete={setDeleteTarget} companyProfile={companyProfile} />
        </div>
      ),
    },
  ], [companyProfile]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Bills</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            placeholder="Search bills, vendors…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 w-[220px] text-sm"
          />
          <Select value={projectFilter} onValueChange={(v) => setProjectFilter(v as any)}>
            <SelectTrigger className="h-9 w-[180px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Bills</SelectItem>
              <SelectItem value="assigned">Assigned to Project</SelectItem>
              <SelectItem value="unassigned">
                Unassigned {needsAssignmentCount > 0 && `(${needsAssignmentCount})`}
              </SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => setUploadOpen(true)} className="gap-1.5">
            <Upload className="h-4 w-4" />
            Upload Bill
          </Button>
          {bills && bills.length > 0 && <ExportToolbar entityType="bills" data={bills} filterDescription="" />}
        </div>
      </div>

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all">All Bills</TabsTrigger>
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="aging">AP Aging</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="pt-2 space-y-3">
          {/* Filter bar */}
          <div className="flex items-end gap-2 flex-wrap rounded-md border bg-muted/30 p-2">
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">Vendor</Label>
              <Input
                placeholder="Vendor name…"
                className="h-8 w-48"
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">Min $</Label>
              <Input
                type="number" inputMode="decimal" placeholder="0"
                className="h-8 w-28"
                value={amountMin}
                onChange={(e) => setAmountMin(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">Max $</Label>
              <Input
                type="number" inputMode="decimal" placeholder="∞"
                className="h-8 w-28"
                value={amountMax}
                onChange={(e) => setAmountMax(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">From</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("h-8 w-40 justify-start text-left font-normal", !dateFrom && "text-muted-foreground")}
                  >
                    <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                    {dateFrom ? format(new Date(dateFrom + "T00:00:00"), "PP") : "From"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateFrom ? new Date(dateFrom + "T00:00:00") : undefined}
                    onSelect={(d) => setDateFrom(d ? format(d, "yyyy-MM-dd") : "")}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                  {dateFrom && (
                    <div className="p-2 border-t">
                      <Button size="sm" variant="ghost" className="w-full h-7" onClick={() => setDateFrom("")}>Clear</Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs text-muted-foreground">To</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn("h-8 w-40 justify-start text-left font-normal", !dateTo && "text-muted-foreground")}
                  >
                    <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                    {dateTo ? format(new Date(dateTo + "T00:00:00"), "PP") : "To"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateTo ? new Date(dateTo + "T00:00:00") : undefined}
                    onSelect={(d) => setDateTo(d ? format(d, "yyyy-MM-dd") : "")}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                  {dateTo && (
                    <div className="p-2 border-t">
                      <Button size="sm" variant="ghost" className="w-full h-7" onClick={() => setDateTo("")}>Clear</Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-8" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>


          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
              Failed to load bills. Please refresh the page.
            </div>
          ) : !bills?.length ? (
            <div className="text-center py-12 text-muted-foreground">
              <CreditCardIcon className="mx-auto h-8 w-8 mb-2 opacity-30" />
              <p>No bills found.</p>
            </div>
          ) : (
            <>
              {/* Mobile card view */}
              <div className="space-y-3 md:hidden">
                {(bills ?? []).map((b) => (
                  <div key={b.id} className={cn("rounded-lg border bg-card p-4 space-y-2 cursor-pointer", b.status === "voided" && "opacity-50")} onDoubleClick={() => setDetailBill(b)}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm font-medium">{b.bill_number}</span>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={b.status} />
                        <BillActions bill={b} onApprove={setApproveTarget} onMarkPaid={setPaidTarget} onVoid={setVoidTarget} onHardDelete={setDeleteTarget} companyProfile={companyProfile} />
                      </div>
                    </div>
                    <div className="text-sm">
                      {b.projects?.name ?? ((b as any).needs_project_assignment ? (
                        <ProjectAllocator bill={b} />
                      ) : "—")}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{b.vendors?.name ?? "—"}</span>
                      <span className="font-medium">{fmt(b.total)}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop DataTable */}
              <div className="hidden md:block">
                <DataTable
                  tableId="bills-list"
                  columns={billColumns}
                  data={bills ?? []}
                  rowClassName={(b) => b.status === "voided" ? "opacity-50" : ""}
                  onRowDoubleClick={(b) => setDetailBill(b)}
                  serverFiltered
                  enableFiltering={false}
                />
              </div>
              <PaginationControls pagination={pagination} />
            </>
          )}
        </TabsContent>

        <TabsContent value="summary" className="pt-2">
          <BillsSummaryTab onBillClick={setDetailBill} />
        </TabsContent>

        <TabsContent value="aging" className="pt-2">
          <APAgingReport />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ApproveConfirm
        bill={approveTarget}
        open={!!approveTarget}
        onClose={() => setApproveTarget(null)}
      />
      <MarkPaidDialog
        bill={paidTarget}
        open={!!paidTarget}
        onClose={() => setPaidTarget(null)}
      />
      <VoidDialog
        bill={voidTarget}
        open={!!voidTarget}
        onClose={() => setVoidTarget(null)}
      />
      <HardDeleteDialog
        bill={deleteTarget}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
      <BillUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onSuccess={() => {}}
      />
      <BillDetailModal
        bill={detailBill}
        open={!!detailBill}
        onClose={() => setDetailBill(null)}
      />
    </div>
  );
};

// Simple fallback icon inline to avoid import issues
const CreditCardIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M3 10h18M7 15h.01M11 15h2M3 6h18a1 1 0 011 1v10a1 1 0 01-1 1H3a1 1 0 01-1-1V7a1 1 0 011-1z" />
  </svg>
);

const Bills = () => (
  <RequirePermission anyOf={["view_financials", "approve_bills", "view_accounts_payable"]}>
    <BillsInner />
  </RequirePermission>
);

export default Bills;
